import {Injectable} from '@angular/core';
import {Observable, throwError} from 'rxjs';
import {catchError, finalize, map} from 'rxjs/operators';

import {EXAMPLE_API_URL, EXAMPLE_DETAIL_API_URL, EXAMPLE_TREE_API_URL, PAGE} from '../../defines/protocol';
import {IsEmptyString, IsEmptyVar} from '../../defines/common.func';
import {SearchParam} from '../../defines/client.const';
import {Example} from '../../models/example.model';
import {ExampleCatTree} from '../../models/example-tree.model';
import {ListLoader, ListLoader_NextList} from '../../interfaces/list-loader';
import {ApiService} from '../api/api.service';
import {NormalizeSearchKeyword} from '../../defines/search-keyword.func';

@Injectable({
  providedIn: 'root'
})
export class ExampleService implements ListLoader<Example> {
  protected urlApi = EXAMPLE_API_URL;

  protected totalCount: number;
  protected pageSize: number;
  protected listExample: Array<Example>;
  protected mapExample: Map<string, Example>;
  protected nextUrl: string;
  private load$: Observable<ListLoader_NextList<Example>> = null;
  private loadDetail$: Observable<Example> = null;
  private loadTree$: Observable<Array<ExampleCatTree>> = null;
  private listTree: Array<ExampleCatTree> = null;

  constructor(
    protected api: ApiService,
  ) {
  }

  public getList(): Array<Example> {
    return this.listExample;
  }

  public getCurCount(): number {
    return IsEmptyVar(this.listExample) ? null : this.listExample.length;
  }

  public getTotalCount(): number {
    return IsEmptyVar(this.totalCount) ? null : this.totalCount;
  }

  public IsComplete(): boolean {
    return this.totalCount === 0 ||
      (!IsEmptyVar(this.listExample) && !IsEmptyVar(this.totalCount) && this.listExample.length === this.totalCount);
  }

  public IsLoading(): boolean {
    return this.load$ !== null;
  }

  public getById(id: string): Example {
    return this.mapExample?.get(id) ?? null;
  }

  public ResetList(param: SearchParam = null): void {
    this.nextUrl = null;
    this.listExample = null;
    this.mapExample = null;
    this.totalCount = null;
    this.pageSize = null;

    const queryParams = new URLSearchParams();

    if (param) {
      if ('keyword' in param) {
        const keyword = NormalizeSearchKeyword(param.keyword);
        if (!IsEmptyString(keyword)) {
          queryParams.set('search', keyword);
        }
      }

      if ('start' in param) queryParams.set('start', param.start);
      if ('pp' in param) {
        queryParams.set('pp', param.pp.toString());
      } else if ('postposition' in param) {
        queryParams.set('pp', param.postposition.toString());
      }
      if ('example_cat' in param) queryParams.set('cat', param.example_cat.toString());
    }

    this.nextUrl = this.urlApi + (queryParams.toString() ? `?${queryParams.toString()}` : '');
  }

  public getTree(): Array<ExampleCatTree> {
    return this.listTree;
  }

  public GetTree(): Observable<Array<ExampleCatTree>> {
    if (this.loadTree$) {
      return this.loadTree$;
    }

    return this.loadTree$ = this.api.Get(EXAMPLE_TREE_API_URL).pipe(
      finalize(() => {
        this.loadTree$ = null;
      }),
      map((data: PAGE) => {
        this.listTree = new Array<ExampleCatTree>();
        data.results.forEach(value => {
          this.listTree.push(new ExampleCatTree(value));
        });
        return this.listTree;
      })
    );
  }

  public GetNextList(): Observable<ListLoader_NextList<Example>> {
    if (IsEmptyString(this.nextUrl)) {
      return throwError(() => ({error: 'No more pages in ExampleList'}));
    }

    if (this.load$) {
      return this.load$;
    }

    return this.load$ = this.api.Get(this.nextUrl).pipe(
      catchError(err => {
        if ('status' in err && err.status === 404) {
          this.totalCount = 0;
        }
        throw err;
      }),
      finalize(() => {
        this.load$ = null;
      }),
      map((data: PAGE) => {
        console.log('ExampleService::GetNextList data =', data);

        let first = false;
        if (this.totalCount === null) {
          this.totalCount = data.count;
          this.pageSize = data.page_size;
          this.listExample = new Array<Example>();
          this.mapExample = new Map<string, Example>();
          first = true;
        }
        else if (this.totalCount !== data.count) {
          console.error('[error]' + this.urlApi, data);
        }

        const list = new Array<Example>();
        data.results.forEach(value => {
          const example = new Example(value);
          list.push(example);
          this.listExample.push(example);
          this.mapExample.set(example.id, example);
        });

        this.nextUrl = data.next;

        return {list, first};
      })
    );
  }

  public GetDetail(exampleId: string): Observable<Example> {
    const url = EXAMPLE_DETAIL_API_URL + exampleId + '/';
    console.log('ExampleService::GetDetail() url =', url);

    return this.loadDetail$ = this.api.Get(url).pipe(
      finalize(() => this.loadDetail$ = null),
      map(value => {
        const out = new Example(value);
        console.log('ExampleService::GetDetail data = ', value, out);
        return out;
      })
    );
  }
}
